import { toIntlLocale } from 'shared/helpers/intlLocale';

export const DAY_MS = 86400000;
export const GRID_START_MIN = 7 * 60;
export const GRID_HOURS = 13;
export const PX_PER_MIN = 0.8;
export const MIN_BLOCK_HEIGHT = 20;
export const LIST_FORWARD_DAYS = 30;

const pad = n => String(n).padStart(2, '0');

export const itemKey = item => `${item.kind || 'appointment'}-${item.id}`;

export const startOfDay = date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

export const endOfDay = date => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

export const addDays = (date, amount) =>
  new Date(startOfDay(date).getTime() + amount * DAY_MS);

export const isoDate = date => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const minutesOfDate = date => {
  const d = new Date(date);
  return d.getHours() * 60 + d.getMinutes();
};

export const formatMinutes = minutes => {
  const safe = Number(minutes) || 0;
  return `${pad(Math.floor(safe / 60))}:${pad(safe % 60)}`;
};

export const parseTimeToMinutes = value => {
  if (!value || !String(value).includes(':')) return null;
  const [hours, minutes] = String(value)
    .split(':')
    .map(part => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export const mondayOf = date => {
  const d = startOfDay(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

export const monthGridStart = anchor => {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return mondayOf(first);
};

export const rangeForView = (
  view,
  { dayOffset = 0, weekOffset = 0, monthOffset = 0, now = new Date() } = {}
) => {
  const today = startOfDay(now);

  if (view === 'day') {
    const day = addDays(today, dayOffset);
    return { from: day.toISOString(), to: endOfDay(day).toISOString() };
  }

  if (view === 'week') {
    const monday = mondayOf(addDays(today, weekOffset * 7));
    return {
      from: monday.toISOString(),
      to: endOfDay(addDays(monday, 6)).toISOString(),
    };
  }

  if (view === 'month') {
    const anchor = new Date(
      today.getFullYear(),
      today.getMonth() + monthOffset,
      1
    );
    const start = monthGridStart(anchor);
    return {
      from: start.toISOString(),
      to: endOfDay(addDays(start, 41)).toISOString(),
    };
  }

  return {
    from: today.toISOString(),
    to: endOfDay(addDays(today, LIST_FORWARD_DAYS)).toISOString(),
  };
};

export const normalizeAgendaItem = raw => {
  const scheduled = raw?.scheduled_at ? new Date(raw.scheduled_at) : null;
  const validScheduled =
    scheduled && !Number.isNaN(scheduled.getTime()) ? scheduled : null;
  const duration = Number(raw?.duration_min);
  const valueRaw = raw?.kanban?.value;
  const value =
    valueRaw === null || valueRaw === undefined || valueRaw === ''
      ? null
      : Number(valueRaw);

  return {
    ...raw,
    scheduled: validScheduled,
    startMin: validScheduled ? minutesOfDate(validScheduled) : 0,
    durationMin: duration > 0 ? duration : 30,
    dayKey: validScheduled ? isoDate(validScheduled) : '',
    displayName: raw?.contact?.name || raw?.title || '',
    responsibleName: raw?.responsible?.name || '',
    responsibleId: raw?.responsible?.id ?? null,
    value: Number.isFinite(value) ? value : null,
    googleCalendarConnectionId:
      raw?.google_calendar_connection_id ??
      raw?.googleCalendarConnectionId ??
      null,
    googleCalendarId: raw?.google_calendar_id ?? raw?.googleCalendarId ?? null,
    connectionScope: raw?.connection_scope ?? raw?.connectionScope ?? null,
    googleEmail: raw?.google_email ?? raw?.googleEmail ?? null,
    canUpdate: raw?.capabilities?.can_update === true,
  };
};

export const canUpdateAgendaItem = item => item?.canUpdate === true;

export const overlaps = (left, right) =>
  left.dayKey === right.dayKey &&
  left.startMin < right.startMin + right.durationMin &&
  right.startMin < left.startMin + left.durationMin;

export const findConflict = (item, items = []) => {
  if (!item || item.status === 'cancelled') return null;
  return (
    items.find(
      other =>
        itemKey(other) !== itemKey(item) &&
        other.status !== 'cancelled' &&
        overlaps(item, other)
    ) || null
  );
};

// O filtro de pessoas virou multi-selecao: 'all' continua valendo e um valor
// unico continua valendo, so que agora tambem aceita lista de ids.
export const responsibleFilterIds = value => {
  if (value == null || value === '' || value === 'all') return [];
  const raw = Array.isArray(value) ? value : String(value).split(',');
  return raw
    .map(entry => Number(entry))
    .filter(entry => Number.isFinite(entry) && entry !== 0);
};

export const matchesResponsibleFilter = (item, value) => {
  const ids = responsibleFilterIds(value);
  if (!ids.length) return true;
  return ids.some(id => Number(item?.responsibleId) === id);
};

export const filterAgendaItems = (
  items,
  { query = '', responsibleId = 'all', onlyCards = false } = {}
) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase();

  return items.filter(item => {
    if (needle) {
      const haystack =
        `${item.contact?.name || ''} ${item.title || ''}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (!matchesResponsibleFilter(item, responsibleId)) return false;
    if (onlyCards && item.source !== 'kanban' && !item.kanban?.item_id)
      return false;
    return true;
  });
};

export const groupByDay = (items, { now = new Date() } = {}) => {
  const map = new Map();
  items.forEach(item => {
    if (!item.dayKey) return;
    if (!map.has(item.dayKey)) map.set(item.dayKey, []);
    map.get(item.dayKey).push(item);
  });

  const todayKey = isoDate(now);
  const nowMin = minutesOfDate(now);

  return [...map.entries()]
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([dayKey, dayItems]) => {
      const sorted = [...dayItems].sort((a, b) => a.startMin - b.startMin);
      const active = sorted.filter(item => item.status !== 'cancelled');
      const cards = active.filter(item => item.value);
      return {
        dayKey,
        date: startOfDay(dayItems[0].scheduled || now),
        items: sorted,
        count: active.length,
        cardSum: cards.reduce((sum, item) => sum + (item.value || 0), 0),
        isToday: dayKey === todayKey,
        nowMin,
      };
    });
};

export const AGENDA_TASKS_PER_DAY = 3;

export const normalizeAgendaTask = raw => {
  const due = raw?.due_at ? new Date(raw.due_at) : null;
  const validDue = due && !Number.isNaN(due.getTime()) ? due : null;
  return {
    id: raw?.id,
    title: raw?.title || '',
    dueAt: validDue,
    dayKey: validDue ? isoDate(validDue) : '',
    startMin: validDue ? minutesOfDate(validDue) : 0,
    overdue: raw?.overdue === true,
    statusKey: raw?.status?.key || '',
    statusLabel: raw?.status?.label || '',
    statusColor: raw?.status?.color || '',
    assigneeId: raw?.assignee?.id ?? null,
    assigneeName: raw?.assignee?.name || '',
    kanbanItemId: raw?.kanban_item_id ?? null,
  };
};

// Faixa "Tarefas" do topo de cada dia: o que cabe, e quantas sobraram no "+N".
export const groupTasksByDay = (
  tasks = [],
  { limit = AGENDA_TASKS_PER_DAY } = {}
) => {
  const map = new Map();
  tasks.forEach(task => {
    if (!task?.dayKey) return;
    if (!map.has(task.dayKey)) map.set(task.dayKey, []);
    map.get(task.dayKey).push(task);
  });

  const grouped = {};
  map.forEach((dayTasks, dayKey) => {
    const sorted = [...dayTasks].sort((left, right) => {
      if (left.overdue !== right.overdue) return left.overdue ? -1 : 1;
      return left.startMin - right.startMin;
    });
    const safeLimit = Math.max(0, Number(limit) || 0);
    grouped[dayKey] = {
      visible: sorted.slice(0, safeLimit),
      extra: Math.max(0, sorted.length - safeLimit),
      total: sorted.length,
    };
  });
  return grouped;
};

export const tasksForDay = (grouped, dayKey) =>
  grouped?.[dayKey] || { visible: [], extra: 0, total: 0 };

// Cor por pessoa: determinística pelo id, para o mesmo responsável manter a
// mesma cor entre recargas. Sem responsável cai no slate legível.
const RESPONSIBLE_TONES = ['blue', 'iris', 'teal', 'amber', 'ruby', 'violet'];

export const responsibleTone = responsibleId => {
  const numeric = Number(responsibleId);
  if (!Number.isFinite(numeric) || numeric === 0) return 'slate';
  return RESPONSIBLE_TONES[
    Math.abs(Math.trunc(numeric)) % RESPONSIBLE_TONES.length
  ];
};

// Classes literais: o Tailwind varre o fonte, entao montar 'bg-n-' + tom em
// tempo de execucao sairia do bundle e a pilula voltaria a ficar invisivel.
const RESPONSIBLE_SOLID_CLASS = {
  blue: 'bg-n-blue-9 text-white border-n-blue-10',
  iris: 'bg-n-iris-9 text-white border-n-iris-10',
  teal: 'bg-n-teal-9 text-white border-n-teal-10',
  amber: 'bg-n-amber-9 text-n-slate-1 border-n-amber-10',
  ruby: 'bg-n-ruby-9 text-white border-n-ruby-10',
  violet: 'bg-n-violet-9 text-white border-n-violet-10',
  slate: 'bg-n-slate-9 text-white border-n-slate-10',
};

export const responsibleSolidClass = responsibleId =>
  RESPONSIBLE_SOLID_CLASS[responsibleTone(responsibleId)] ||
  RESPONSIBLE_SOLID_CLASS.slate;

export const showNowBefore = (group, index, nowMin) => {
  if (!group?.isToday) return false;
  const item = group.items[index];
  if (!item || item.startMin < nowMin) return false;
  return index === 0 || group.items[index - 1].startMin < nowMin;
};

export const showNowAfterAll = (group, nowMin) =>
  Boolean(group?.isToday && group.items.every(item => item.startMin < nowMin));

export const isPastItem = (item, now = new Date()) => {
  if (!item?.scheduled) return false;
  const end = new Date(item.scheduled.getTime() + item.durationMin * 60000);
  return end <= now;
};

export const needsPresence = (item, now = new Date()) =>
  isPastItem(item, now) && item.status === 'scheduled';

export const minutesUntilStart = (item, now = new Date()) => {
  if (!item?.scheduled || item.status !== 'scheduled') return null;
  const diff = Math.round((item.scheduled.getTime() - now.getTime()) / 60000);
  return diff > 0 && diff <= 60 ? diff : null;
};

export const assignLanes = items => {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin);
  const placed = sorted.map(item => ({ item, lane: 0 }));

  placed.forEach((entry, index) => {
    const used = new Set(
      placed
        .slice(0, index)
        .filter(other => overlaps(other.item, entry.item))
        .map(other => other.lane)
    );
    let lane = 0;
    while (used.has(lane)) lane += 1;
    entry.lane = lane;
  });

  return placed.map(entry => ({
    ...entry,
    half: placed.some(
      other =>
        itemKey(other.item) !== itemKey(entry.item) &&
        overlaps(other.item, entry.item)
    ),
  }));
};

export const blockStyle = block => {
  let left = '2%';
  if (block.half && block.lane !== 0) left = '51%';
  return {
    top: `${Math.max(0, (block.item.startMin - GRID_START_MIN) * PX_PER_MIN)}px`,
    height: `${Math.max(block.item.durationMin * PX_PER_MIN - 2, MIN_BLOCK_HEIGHT)}px`,
    left,
    width: block.half ? '47%' : '96%',
  };
};

export const isResponsibleAssignmentLocked = role => role !== 'administrator';

export const isResponsibleFilterLocked = isResponsibleAssignmentLocked;

export const canUseResponsibleDirectory = ({
  role,
  isSupervisor = false,
} = {}) => role === 'administrator' || Boolean(isSupervisor);

const responsibleOption = (id, name) => ({
  id,
  name: name || '',
});

export const assignmentResponsibles = ({
  agents = [],
  currentUser,
  useDirectory = false,
} = {}) => {
  if (useDirectory) {
    return agents.map(agent =>
      responsibleOption(agent.id, agent.name || agent.available_name)
    );
  }
  const self = agents.find(
    agent => Number(agent.id) === Number(currentUser?.id)
  );
  if (self) {
    return [responsibleOption(self.id, self.name || self.available_name)];
  }
  if (currentUser?.id) {
    return [
      responsibleOption(
        currentUser.id,
        currentUser.name || currentUser.available_name
      ),
    ];
  }
  return [];
};

export const filterResponsibles = ({
  items = [],
  agents = [],
  currentUser,
  useDirectory = false,
} = {}) => {
  if (useDirectory) {
    return assignmentResponsibles({ agents, currentUser, useDirectory: true });
  }
  const byId = new Map();
  const add = (id, name) => {
    if (id == null || id === '') return;
    const numeric = Number(id);
    if (!Number.isFinite(numeric) || byId.has(numeric)) return;
    byId.set(numeric, responsibleOption(id, name));
  };
  const self = agents.find(
    agent => Number(agent.id) === Number(currentUser?.id)
  );
  add(
    self?.id ?? currentUser?.id,
    self?.name ||
      self?.available_name ||
      currentUser?.name ||
      currentUser?.available_name
  );
  items.forEach(item => {
    add(
      item.responsibleId ?? item.responsible?.id,
      item.responsibleName || item.responsible?.name
    );
  });
  return [...byId.values()];
};

export const sanitizeResponsibleFilter = ({
  requested,
  allowedIds = [],
} = {}) => {
  if (requested == null || requested === '' || requested === 'all') {
    return 'all';
  }
  if (Array.isArray(requested)) {
    const kept = responsibleFilterIds(requested).filter(id =>
      allowedIds.some(allowed => Number(allowed) === id)
    );
    return kept.length ? kept : 'all';
  }
  return allowedIds.some(id => Number(id) === Number(requested))
    ? requested
    : 'all';
};

export const resolveResponsibleFilter = ({
  requested = 'all',
  allowedIds,
} = {}) => {
  if (Array.isArray(allowedIds)) {
    return sanitizeResponsibleFilter({ requested, allowedIds });
  }
  if (requested == null || requested === '') return 'all';
  return requested;
};

export const isCurrentGoogleCalendarAccessEvent = (
  event,
  { accountId, userId } = {}
) => {
  if (!event || typeof event !== 'object') return false;
  if (event.account_id == null || event.user_id == null) return false;
  if (String(event.account_id) !== String(accountId)) return false;
  if (String(event.user_id) !== String(userId)) return false;
  return event.connection_id != null && event.connection_id !== '';
};

export const rejectAgendaItemsByConnection = (
  items = [],
  connectionId = null
) => {
  if (connectionId == null || connectionId === '') return items;
  return items.filter(
    item =>
      String(
        item.googleCalendarConnectionId ?? item.google_calendar_connection_id
      ) !== String(connectionId)
  );
};

export const rejectConnectionsById = (
  connections = [],
  connectionId = null
) => {
  if (connectionId == null || connectionId === '') return connections;
  return connections.filter(
    connection => String(connection?.id) !== String(connectionId)
  );
};

export const isScheduleReady = ({
  dayResolved,
  startResolved,
  responsibleId,
  requireResponsible = true,
}) =>
  dayResolved != null &&
  startResolved != null &&
  (!requireResponsible || Boolean(responsibleId));

export const buildScheduledAt = (dayDate, startMin) => {
  const next = new Date(dayDate);
  next.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
  return next.toISOString();
};

export const formatMoney = (value, locale = 'pt-BR') => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return '';
  const currency = String(locale).toLowerCase().startsWith('pt')
    ? 'BRL'
    : 'USD';
  return new Intl.NumberFormat(toIntlLocale(locale), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const googleUiStatus = connection => {
  if (connection?.sync_mode === 'disabled') return 'none';
  if (!connection || connection.connected === false) return 'none';
  if (connection.status === 'active' || connection.usable === true) {
    return 'active';
  }
  return 'error';
};

export const CALENDAR_DESTINATION_DEFAULT = '';
export const CALENDAR_DESTINATION_UNAVAILABLE = '__unavailable__';

export const isWritableCalendarConnection = connection =>
  Boolean(
    connection &&
      connection.id != null &&
      connection.usable === true &&
      connection.capabilities?.can_write_events === true
  );

export const writableCalendarConnections = (connections = []) =>
  (Array.isArray(connections) ? connections : []).filter(
    isWritableCalendarConnection
  );

export const destinationStillWritable = (destinationId, connections) => {
  if (
    destinationId == null ||
    destinationId === CALENDAR_DESTINATION_DEFAULT ||
    destinationId === CALENDAR_DESTINATION_UNAVAILABLE
  ) {
    return false;
  }
  return writableCalendarConnections(connections).some(
    connection => String(connection.id) === String(destinationId)
  );
};

export const explicitCalendarConnectionId = destinationId => {
  if (
    destinationId == null ||
    destinationId === CALENDAR_DESTINATION_DEFAULT ||
    destinationId === CALENDAR_DESTINATION_UNAVAILABLE
  ) {
    return null;
  }
  const parsed = Number(destinationId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const nextBusinessOffset = (fromOffset = 2, now = new Date()) => {
  let offset = fromOffset;
  while (addDays(now, offset).getDay() === 0) offset += 1;
  return offset;
};

// Linha do tempo de um dia: blocos ocupados na ordem, com as janelas livres
// entre eles. É o que a central de agendamento mostra — o operador enxerga o
// buraco ("livre 10:00–11:30") em vez de ter que deduzir olhando a grade.
export const buildDayTimeline = (
  items = [],
  { dayStartMin = 8 * 60, dayEndMin = 19 * 60, minGapMin = 30 } = {}
) => {
  const busy = [...items]
    .filter(item => item.status !== 'cancelled')
    .sort((left, right) => left.startMin - right.startMin);

  const rows = [];
  let cursor = dayStartMin;

  busy.forEach(item => {
    const start = item.startMin;
    const end = start + item.durationMin;
    // A faixa livre para no fim do expediente: um compromisso as 20:00 abria
    // uma faixa 08:00-20:00 e a central oferecia 19:00 e 19:30.
    const freeEnd = Math.min(start, dayEndMin);
    if (freeEnd - cursor >= minGapMin) {
      rows.push({ type: 'free', startMin: cursor, endMin: freeEnd });
    }
    rows.push({ type: 'event', startMin: start, endMin: end, item });
    cursor = Math.max(cursor, end);
  });

  if (dayEndMin - cursor >= minGapMin) {
    rows.push({ type: 'free', startMin: cursor, endMin: dayEndMin });
  }
  return rows;
};

// Primeiros horários livres do dia, varrendo a grade em passos de stepMin e
// pulando os que colidem com compromissos ativos. notBeforeMin corta horários
// que já passaram quando o dia escolhido é hoje.
export const findFreeSlots = (
  items = [],
  {
    dayKey,
    durationMin = 30,
    startMin = 8 * 60,
    endMin = 19 * 60,
    stepMin = 30,
    notBeforeMin = null,
    limit = 3,
  } = {}
) => {
  if (!dayKey) return [];
  const active = items.filter(item => item.status !== 'cancelled');
  const slots = [];
  for (let min = startMin; min + durationMin <= endMin; min += stepMin) {
    if (notBeforeMin != null && min < notBeforeMin) continue; // eslint-disable-line no-continue
    const probe = { dayKey, startMin: min, durationMin };
    if (!active.some(item => overlaps(probe, item))) slots.push(min);
    if (slots.length >= limit) break;
  }
  return slots;
};
